import { useEffect, useState } from 'react';
import {
  useMarkNotificationMutation,
  useNotificationListQuery,
} from 'common/cache';
import { useNotificationsEvents } from 'common/hooks';
import { toast } from 'react-toastify';
import { formatApiError } from 'common/errors/error-formatting.ts';
import { NotificationToast } from 'components/Header/components/Notifications/NotificationToast.tsx';
import { useNavigate } from 'react-router-dom';
import { useDrawerStore } from 'store';

const notificationTimer = 9000;

export const useNotifications = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const { setDrawerOpen, setTaskId } = useDrawerStore(state => state);
  const { event } = useNotificationsEvents();

  const {
    data = {
      count: 0,
    },
    refetch,
  } = useNotificationListQuery({
    props: { limit: 100, page: 1, status: 'unread' },
  });

  const { mutateAsync: markNotificationAsync } = useMarkNotificationMutation();

  const handleMarkNotification = async (id: string) => {
    try {
      await markNotificationAsync({ id, variant: 'read' });
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const viewTask = async ({
    task_id,
    patient_id,
    notification_id,
  }: {
    patient_id: string | null;
    task_id: string | null;
    notification_id: string;
  }) => {
    if (!patient_id || !task_id) {
      return;
    }
    await handleMarkNotification(notification_id);
    navigate(`/patient/${patient_id}`);
    setTaskId(task_id);
    setDrawerOpen(true);
  };

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (event && event.type === 'notification.created') {
      toast(
        <NotificationToast
          description={event.payload.notification.description || ''}
          makeAsReadAction={() =>
            handleMarkNotification(event?.payload.notification.id || '')
          }
          viewTaskAction={() =>
            viewTask({
              notification_id: event?.payload.notification.id,
              task_id: event?.payload.notification.task_id,
              patient_id: event?.payload.notification.patient_id,
            })
          }
        />,
        { autoClose: notificationTimer },
      );
      refetch();
    }
  }, [event]);

  return {
    anchorEl,
    id: anchorEl ? 'notification-popover' : undefined,
    handleOpen,
    handleClose,
    isOpen: !!anchorEl,
    unreadNotificationCount: data.count,
  };
};
